<template>
  <b-modal
    id="filter-modal"
    v-model="show"
    @show="getBooks"
    @hide="hidePopup"
    @keydown.enter="save"
    size="xl"
    body-class="text-right"
    modal-class=""
    header-class="text-white bg-primary position-sticky"
    hideFooter
    ok-only
    ref="filterModal"
  >
    <template slot="modal-header">
      <div>
        <i class="fas fa-sliders-h" :class="hebrew ? 'pl-2' : 'pr-2'"></i>
        {{ hebrew ? "מיון תוצאות" : "Sort Results" }}
      </div>
      <b-btn class="" variant="outline-light" @click="save">
        <span v-if="metaData">{{ hebrew ? "ביטול" : "Cancel" }}</span>
        <span v-else>{{ hebrew ? "שמור וסגור" : "Save and close" }}</span>
      </b-btn>
    </template>
    <b-tabs v-model="tabIndex" content-class="py-3" nav-class="py-0 mx-0 px-4">
      <b-tab
        :title="hebrew ? 'טבלה' : 'Table'"
        title-link-class="py-0 mt-2 px-4 text-body"
      >
        <div
          v-if="!metaData"
          class="form-wrap bg-background p-3 mb-4 pt-5 pt-lg-3 pl-lg-2 pb-lg-1"
          :class="hebrew ? 'text-right' : 'text-left'"
          :dir="hebrew ? 'rtl' : 'ltr'"
        >
          <b-row class="mb-3">
            <b-col class="pl-xl-4 b-left" cols="12" lg="6" xl="4">
              <label for="book-name">{{
                hebrew ? "שם הספר" : "Book name"
              }}</label>
              <b-form-input id="book-name" v-model="bookName"></b-form-input>
            </b-col>
            <b-col class="px-xl-4 b-left author-col" cols="12" lg="6" xl="4">
              <label for="book-author">{{ hebrew ? "מחבר" : "Author" }}</label>
              <b-form-input
                id="book-author"
                v-model="bookAuthor"
              ></b-form-input>
            </b-col>
            <b-col cols="12" lg="6" xl="4" class="px-xl-4">
              <b-row class="align-items-end">
                <b-col class="pl-1">
                  <label for="from-year">{{ hebrew ? "משנה" : "Year" }}</label>
                  <b-form-input
                    id="from-year"
                    v-model="fromYear"
                  ></b-form-input>
                </b-col>
                <b-col class="pr-lg-1">
                  <b-form-checkbox v-model="toYearEnabled">
                    עד שנה
                  </b-form-checkbox>
                  <!--:class="{'disabled':!toYearEnabled}"-->
                  <b-form-input
                    id="to-year"
                    v-model="toYear"
                    :disabled="!toYearEnabled"
                  ></b-form-input>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row class="pb-2">
            <b-col cols="12" lg="6" xl="4" class="pl-xl-4 pr-xl-3">
              <label>{{ hebrew ? "מקום הדפסה" : "Location" }}</label>
              <b-dropdown
                :disabled="locations.length === 0"
                class="d-dropdown d-inline-block bg-white flex-grow-1 w-100"
                toggle-class="bg-white text-dark shadow-none"
                variant="outline-dark"
                right
                @hide="preventDropdownClose"
                ref="locationDD"
              >
                <template #button-content>
                  <span
                    >{{
                      printLocation.length > 1
                        ? `(${printLocation.length})`
                        : ""
                    }}
                    {{
                      printLocation.length > 0
                        ? printLocation.join(", ")
                        : "בחירת מקום"
                    }}</span
                  >
                </template>
                <b-dropdown-form>
                  <b-form-group>
                    <b-form-checkbox
                      v-model="printLocationSelectAll"
                      @change="togglePrintLocationSelectAll"
                      :indeterminate="printLocationIsIndeterminate"
                    >
                      <b>{{ hebrew ? "הכל" : "Select All" }}</b>
                    </b-form-checkbox>
                    <b-form-checkbox-group v-model="printLocation" stacked>
                      <span v-for="option in locations" :key="option">
                        <b-form-checkbox v-if="option" :value="option"
                          ><!--@change="toggleOption(option, 'printLocation', locations)"-->
                          {{ option }}
                        </b-form-checkbox>
                      </span>
                    </b-form-checkbox-group>
                  </b-form-group>
                </b-dropdown-form>
              </b-dropdown>
            </b-col>
            <b-col class="d-flex align-self-end justify-content-end pl-xl-4">
              <b-btn
                id="clear-btn"
                variant="link"
                class="bg-transparent p-0 mt-3"
                @click="clearFilter"
                ><u>איפוס סינון</u></b-btn
              >
            </b-col>
          </b-row>
        </div>
        <small class="book-count mb-2 d-block" v-if="totalRows > 0">
          {{ hebrew ? "מציג" : "Showing" }}
          <b
            >{{ currentPage }} -
            {{
              totalRows === -1 || totalRows >= perPage
                ? currentPage * perPage
                : totalRows
            }}</b
          >
          {{ hebrew ? "מתוך" : "" }}
          <b>{{ totalRows > -1 ? totalRows : "" }}</b>
          {{ hebrew ? "ספרים" : "Books" }}
        </small>
        <small class="book-count mb-2 d-block" v-else>{{
          hebrew ? "מציג 0 תוצאות" : "0 Results"
        }}</small>

        <div v-if="!metaData" class="selected-books p-2 mb-3">
          <i
            class="fas fa-plus-square d-inline-block align-top mt-1"
            :class="{ 'text-muted': !isOverflowing }"
            v-if="!showFullText"
            @click="showFullText = true"
          ></i>
          <i
            class="fas fa-minus-square d-inline-block align-top mt-1"
            v-else
            @click="showFullText = false"
          ></i>
          <div
            class="book-names d-inline-block align-text-top mr-2 m"
            :class="{ overflow: showFullText }"
          >
            <p ref="displayText" class="overflow-auto h-100 m-0">
              <small
                class="d-block"
                :class="{ 'text-muted': selectedBooks.length === 0 }"
                ><b
                  >{{ hebrew ? "ספרים שנבחרו" : "Selected books" }} ({{
                    selectedBooks.length
                  }})<span v-if="selectedBooks.length > 0">:</span></b
                >
                {{ selectedBooks.map((el) => el.title).join(", ") }}</small
              >
            </p>
          </div>
        </div>
        <b-table
          responsive
          dir="rtl"
          :current-page="currentPage"
          :per-page="perPage"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          selectable
          select-mode="multi"
          :selected-rows="selectedRows"
          @row-selected="handleRowSelected"
          :items="filteredItems"
          ref="sourceTable"
        >
          <template v-if="!metaData" #head(selected)>
            <b-form-checkbox
              v-model="selectAll"
              :indeterminate="isIndeterminate"
              @change="toggleSelectAll"
            >
            </b-form-checkbox>
          </template>
          <template v-if="!metaData" #cell(selected)="row">
            <b-form-checkbox
              v-model="selectedRows"
              :value="row.item"
              :name="row.index + ''"
            ></b-form-checkbox>
          </template>
        </b-table>
        <div class="text-center pagination-wrap" v-if="totalRows > 0">
          <b-pagination
            v-model="currentPage"
            hide-ellipsis
            :total-rows="totalRows"
            :per-page="perPage"
            limit="10"
            prev-text="לעמוד  הקודם"
            next-text="לעמוד הבא"
            aria-controls="book-table"
            hide-goto-end-buttons
            page-class="num-btn"
            next-class="text-btn"
            prev-class="text-btn"
          >
          </b-pagination>
        </div>
      </b-tab>
      <b-tab
        :title="hebrew ? 'ציר זמן' : 'Timeline'"
        title-link-class="py-0 mt-2 px-4 text-body"
      >
        <visualizer
          v-if="tabIndex == 1"
          :metaData="filteredItems"
          :singleClicked="metaData"
          :range="yearRange"
        >
        </visualizer>
      </b-tab>
      <b-tab
        :title="hebrew ? 'מפה' : 'Map'"
        title-link-class="py-0 mt-2 px-4 text-body"
      >
        <mapped-data v-if="tabIndex == 2" :metaData="filteredItems">
        </mapped-data>
      </b-tab>
      <b-tab
        v-if="!metaData"
        :title="hebrew ? 'הגדרות נוספות' : 'Additional Settings'"
        title-link-class="py-0 mt-2 px-4 text-body"
      >
        <b-form-checkbox
          v-model="localShowEarly"
          name="show-early"
          class="mt-3 mb-2"
        >
          <span>
            {{
              hebrew ? "הצגת מקורות מוקדמים בלבד" : "Display early sources only"
            }}
          </span>
        </b-form-checkbox>
        <b-form-checkbox
          v-model="localHideCanocial"
          name="hide-short-canonical"
        >
          <span>
            {{
              hebrew
                ? 'לא להציג מקורות קצרים מהתנ"ך ומהתלמוד'
                : "Hide short canonical results"
            }}
          </span>
        </b-form-checkbox>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<script>
import bookList from "./MetaDataSefaria.json"
import bookListBY from "./MetaDataBY.json"
import Visualizer from "@/components/Visualizer"
import MappedData from "@/components/MappedData"

export default {
  name: "SourceFilterPopup",
  props: ["value", "metaData", "showEarly", "hideCanocial"],
  components: { Visualizer, MappedData },
  data() {
    return {
      options: ["Option 1", "Option 2", "Option 3", "Option 4"],
      selectedRows: [],
      fields: [
        { key: "selected", label: "", sortable: false },
        {
          key: "library",
          label: "ספריה",
          sortable: true,
          sortDirection: "asc",
        },
        { key: "title", label: "מקור", sortable: true, sortDirection: "asc" },
        { key: "period", label: "תקופה", sortable: true, sortDirection: "asc" },
        {
          key: "year",
          label: "שנת הדפסה",
          sortable: true,
          sortDirection: "asc",
        },
        { key: "city", label: "עיר", sortable: true, sortDirection: "asc" },
        {
          key: "location",
          label: "מדינה",
          sortable: true,
          sortDirection: "asc",
        },
        { key: "author", label: "מחבר", sortable: true, sortDirection: "asc" },
        /* { key: 'category', label: 'קטגוריה', sortable: true, sortDirection: 'asc' },
        { key: 'subcategory', label: 'תת קטגוריה', sortable: true, sortDirection: 'asc' }, */
      ],
      corpus: bookList,
      currentPage: 1,
      perPage: 10,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      bookName: "",
      bookAuthor: "",
      selectedCategory: [],
      categorySelectAll: false,
      selectedSubCategory: [],
      subCategorySelectAll: false,
      fromYear: "",
      toYear: "",
      toYearEnabled: false,
      printLocation: [],
      printLocationSelectAll: false,
      //  filteredItems: [],
      selectAll: false,
      pageSelections: [],
      selectedBooks: [],
      showFullText: false,
      isOverflowing: false,
      isIndeterminate: false,
      tabIndex: 0,
      yearRange: [],
      localShowEarly: false,
      localHideCanocial: false,
    }
  },
  created() {
    // Initialize page selections array
    //this.pageSelections[this.currentPage] = this.selectedRows
  },
  methods: {
    updateSelectedRows() {
      if (this.selectedBooks.length > 0) {
        const displayedItems = this.pagedItems
        const selectedBooksInDisplayed = this.selectedBooks.filter((book) =>
          displayedItems.some((item) => item.xmlId === book.xmlId)
        )
        this.selectedRows = selectedBooksInDisplayed
        this.updateSelectAll()
      }
    },
    togglePrintLocationSelectAll() {
      if (this.printLocationSelectAll && !this.printLocationIsIndeterminate) {
        this.printLocation = this.locations.slice()
      } else {
        this.printLocation = []
        this.printLocationSelectAll = false // if !this.printLocationIsIndeterminate
      }
    },
    toggleCategorySelectAll() {
      if (this.categorySelectAll && !this.categoryIsIndeterminate) {
        this.selectedCategory = this.categories.slice()
      } else {
        this.selectedCategory = []
        this.categorySelectAll = false
      }
    },
    toggleSubCategorySelectAll() {
      if (this.subCategorySelectAll && !this.subCategoryIsIndeterminate) {
        this.selectedSubCategory = this.subCategories.slice()
      } else {
        this.selectedSubCategory = []
        this.subCategorySelectAll = false
      }
    },

    handleRowSelected(items) {
      this.selectedRows = items
    },
    toggleSelectAll() {
      if (this.selectAll && !this.isIndeterminate) {
        this.selectedRows = [...this.selectedRows, ...this.pagedItems]
      } else {
        //const currentPageItems = this.selectedRows.filter(item => !this.pagedItems.includes(item))
        const currentPageItems = this.selectedRows.filter(
          (item) =>
            !this.pagedItems.some((pagedItem) => pagedItem.xmlId === item.xmlId)
        )
        this.selectedRows = currentPageItems
        this.checkTableIsInderterminate()
      }
    },
    updateSelectAll() {
      this.checkIfAllSelected()
      if (this.selectAll) this.isIndeterminate = false
      else this.checkTableIsInderterminate()
    },
    checkIfAllSelected() {
      const currentPageItems = this.pagedItems
      const allCurrentPageItemsSelected = currentPageItems.every((item) =>
        this.selectedRows.some((row) => row.xmlId === item.xmlId)
      )
      this.selectAll = allCurrentPageItemsSelected
    },
    checkTableIsInderterminate() {
      const currentPageItems = this.pagedItems
      this.isIndeterminate = currentPageItems.some((item) =>
        this.selectedRows.some((row) => row.xmlId === item.xmlId)
      )
      /* const currentPageItems = this.pagedItems;
        const currentPageSelectedItems = this.selectedRows.filter(item =>
          currentPageItems.includes(item)
        );
        this.isIndeterminate = (
          currentPageSelectedItems.length > 0 &&
          currentPageSelectedItems.length < currentPageItems.length
        ) */
    },
    preventDropdownClose(evt) {
      if (evt && evt.relatedTarget && evt.relatedTarget.tagName === "INPUT") {
        evt.preventDefault()
      }
    },

    handleModalScroll() {
      this.$refs.locationDD.hide()
    },
    getBooks() {
      if (this.metaData) {
        this.clearFilter()
      }
      this.localShowEarly = this.showEarly
      this.localHideCanocial = this.hideCanocial
      //console.log([...new Set(this.xmlIDs)])
      let list = bookList.concat(bookListBY)
      this.corpus = list.filter((item) => {
        // Check if either 'ID' or 'compBookXmlId' property exists in xmldIDs array
        return (
          this.xmlIDs.includes(item.ID) ||
          this.xmlIDs.includes(item.compBookXmlId)
        )
      })
      const modal = this.$refs.filterModal
      modal.$on("shown", () => {
        const modalBody = document.getElementById("filter-modal")
        modalBody.addEventListener("scroll", this.handleModalScroll)
      })
    },
    save() {
      this.show = false
      let selectedBookIDs = []
      this.selectedBooks.map((el) => {
        selectedBookIDs.push(el.xmlId)
      })
      this.$emit("selectedBooks", selectedBookIDs)
      this.$emit("settings", {
        hideCanocial: this.localHideCanocial,
        early: this.localShowEarly,
      })
    },

    hidePopup() {
      this.show = false
      history.pushState(
        "",
        document.title,
        window.location.pathname + window.location.search
      )
    },
    clearFilter() {
      this.bookName = ""
      this.bookAuthor = ""
      this.fromYear = ""
      this.toYear = ""
      this.printLocation = []
    },
  },
  computed: {
    xmlIDs() {
      return this.metaData
        ? this.metaData
        : this.returnedData.map((item) => {
            return item.compBookXmlId.replace("benyehuda.", "")
          })
    },
    returnedData() {
      return this.showEarly ? this.earlyPrint : this.$store.state.data
    },
    earlyPrint() {
      let itemsWithYear = this.$store.state.data.filter(
        (item) => parseInt(item.year) > 0
      )
      let earliestYear = Math.min(...itemsWithYear.map((item) => item.year))
      return itemsWithYear.filter(
        (item) =>
          item.year &&
          item.year >= earliestYear &&
          item.year <= earliestYear + 25
      )
    },
    printLocationIsIndeterminate() {
      return (
        this.printLocation.length > 0 &&
        this.printLocation.length < this.locations.length
      )
    },
    categoryIsIndeterminate() {
      return (
        this.selectedCategory.length > 0 &&
        this.selectedCategory.length < this.categories.length
      )
    },
    subCategoryIsIndeterminate() {
      return (
        this.selectedSubCategory.length > 0 &&
        this.selectedSubCategory.length < this.subCategories.length
      )
    },
    hebrew() {
      return this.$settings.hebrew
    },
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
    returnedXmlIDs() {
      let bookResults = JSON.parse(
        JSON.stringify(this.$store.state.corpusBooksState)
      )
      return bookResults.map((item) =>
        item.xmlId.replace("Sefaria_Corpus.", "")
      )
    },
    filteredItems() {
      let filteredItems = JSON.parse(JSON.stringify(this.items))
      if (this.printLocation.length > 0) {
        // && this.printLocation !== 'הכל'
        filteredItems = filteredItems.filter(
          (item) => this.printLocation.indexOf(item.location) > -1
        )
      }

      if (this.bookName) {
        filteredItems = filteredItems.filter((item) =>
          item.title.startsWith(this.bookName)
        )
      }
      if (this.bookAuthor) {
        filteredItems = filteredItems.filter((item) =>
          item.author.startsWith(this.bookAuthor)
        )
      }
      if (this.fromYear) {
        filteredItems = filteredItems.filter(
          (item) => parseInt(item.year) >= parseInt(this.fromYear)
        )
      }
      if (this.toYear) {
        filteredItems = filteredItems.filter(
          (item) => parseInt(item.year) <= parseInt(this.toYear)
        )
      }
      return filteredItems
    },

    /*     tableItems() {
            return this.filteredItems.length > 0 ? this.filteredItems : this.items
          }, */
    items() {
      let arr = []
      this.corpus.map((book) => {
        const obj = {
          /*
            library = element.ID ? 'בן יהודה' : 'ספריא'
            id = element.ID ? element.ID :element.compBookXmlId
            source = element.Hebrew_Name
            period = element.Period
            year = element.Year !== 'null' && element.Year.length > 0 ? parseInt(element.Year.replace('.birth','')) : ''
            city = element.Location_City_1
            country = element.Location_State_1
            author = element.Author_Hebrew
            Lat_Long = element.Lat_Long ? element.Lat_Long : null
          */
          library: book.ID ? "בן יהודה" : "ספריא",
          title: book.Hebrew_Name
            ? book.Hebrew_Name
            : book.HEB_Title
            ? book.HEB_Title
            : "",
          author: book.Author_Hebrew,
          category: book.Sefaria_Main_Cat
            ? book.Sefaria_Main_Cat
            : book.Category
            ? book.Category
            : "",
          subcategory: book.Sefaria_Subcat_1 ? book.Sefaria_Subcat_1 : "",
          year: book.Year,
          city: book.Location_City_1,
          location: book.Location_State_1,
          xmlId: book.ID ? book.ID : book.compBookXmlId,
          Lat_Long: book.Lat_Long ? book.Lat_Long : "",
          period: book.Period,
        }
        arr.push(obj)
      })
      return arr
    },
    totalRows() {
      return this.filteredItems.length
    },

    locations() {
      let locations = this.items.map((item) => item.location) //.filter(str => Boolean(str))
      //  locations.unshift('הכל')
      return [...new Set(locations)]
    },
    pagedItems() {
      const startIndex = (this.currentPage - 1) * this.perPage
      const endIndex = startIndex + this.perPage
      return this.filteredItems.slice(startIndex, endIndex)
    },
  },
  watch: {
    selectedRows(newVal) {
      const currentPageItems = this.pagedItems

      //handle de-selection
      let currentPageItemsXmlIds = new Set(
        currentPageItems.map((item) => item.xmlId)
      )

      // Remove items from this.selectedBooks that are also in currentPageItems
      this.selectedBooks = this.selectedBooks.filter(
        (book) => !currentPageItemsXmlIds.has(book.xmlId)
      )

      this.selectedBooks = [...new Set([...this.selectedBooks, ...newVal])]

      const allCurrentPageItemsSelected = currentPageItems.every((item) =>
        newVal.includes(item)
      )
      this.selectAll = allCurrentPageItemsSelected
      if (!this.selectAll) this.checkTableIsInderterminate()

      setTimeout(() => {
        const displayText = this.$refs.displayText
        this.isOverflowing = displayText.scrollHeight > displayText.clientHeight
      }, 10)

      if (this.selectAll) this.isIndeterminate = false
    },
    currentPage() {
      this.updateSelectedRows()
    },
    filteredItems() {
      this.updateSelectedRows()
    },
  },
}
</script>
<style lang="scss">
#filter-modal {
  @media (min-width: 1200px) {
    .modal-xl {
      max-width: 80%;
    }
  }

  .position-sticky {
    top: 0;
    z-index: 1;
  }

  label {
    font-size: 14px;
    margin-bottom: 5px;
  }

  .author-input {
    &::after,
    &::before {
      width: 1px;
      height: 28px;
      content: "";
      position: absolute;
      left: 0;
      top: 30px;
      background-color: #979797;
    }

    &::before {
      right: 0;
    }
  }

  .b-dropdown {
    &.disabled {
      .btn.dropdown-toggle {
        pointer-events: none;
        background-color: #c6c6c6;
      }
    }

    .btn.dropdown-toggle {
      width: 100%;
      text-align: right;
      padding: 3px 10px;
      overflow: hidden;

      span {
        max-width: 95%;
        display: block;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }

    .dropdown-toggle:after {
      border: none;
      content: "\f107";
      font-family: "Font Awesome 5 Free";
      font-size: 15px;
      position: absolute;
      left: 5px;
      top: 3px;
      font-weight: 900;
    }
  }

  .dropdown-menu {
    //left: 100%;
    width: 100%;
    max-height: 250px;
    overflow-y: scroll;
    padding-top: 3px;

    li {
      .dropdown-item {
        padding: 2px 10px 2px 0;

        &.active {
          color: black !important;
          background-color: #d6ecff !important;
          position: relative;

          &::after {
            content: "\F00c";
            font-family: "Font Awesome 5 Free";
            font-size: 12px;
            position: absolute;
            left: 10px;
            top: 5px;
            font-weight: 900;
          }
        }
      }
    }
  }

  .b-dropdown-form {
    padding: 0.25rem 0.75rem;
  }

  .modal-header {
    direction: rtl;
    line-height: 28px;
  }

  .selected-books {
    border-radius: 4px;
    border: solid 1px #d6d6d6;
    background-color: #fefdec;

    &.text-muted {
      opacity: 0.4;
    }

    i {
      cursor: pointer;

      &.text-muted {
        pointer-events: none;
      }
    }

    .book-names {
      height: 20px;
      overflow-y: hidden;
      //white-space: nowrap;
      text-overflow: ellipsis;
      -ms-overflow-style: none;
      scrollbar-width: none;
      width: 97%;

      ::-webkit-scrollbar {
        display: none;
      }

      &.overflow {
        height: auto;
      }

      small {
        line-height: 20px;
      }
    }
  }

  @media (min-width: 1200px) {
    .table-responsive {
      min-height: 55vh;
    }
  }

  .table.b-table > tbody > .table-active,
  .table.b-table > tbody > .table-active > th,
  .table.b-table > tbody > .table-active > td,
  .table.b-table > tbody > .table-active {
    background-color: transparent !important;
  }

  .table td,
  .table th {
    min-width: 150px;

    &:first-of-type {
      min-width: 20px;
      padding-left: 0;
    }
  }

  .table.b-table thead tr th {
    padding-top: 6px;
    padding-bottom: 6px;
    background-color: #d6ecff;
    border: none;
    color: #646464;
    font-weight: normal;
  }

  thead {
    tr {
      th {
        position: relative;
        padding-right: 0.75rem;

        &:last-child {
          padding-left: 20px;
        }

        &[aria-sort] {
          &::before {
            content: "\f0d8";
            font-family: "Font Awesome 5 Free";
            font-weight: 900;
            position: absolute;
            font-size: 24px;
            top: -4px;
            right: 50%;
          }

          &::after {
            content: "\f0d7";
            font-family: "Font Awesome 5 Free";
            font-weight: 900;
            position: absolute;
            font-size: 24px;
            top: 6px;
            right: 50%;
          }

          &:nth-child(2) {
            &::before,
            &::after {
              right: 85px;
            }
          }

          &:nth-child(3) {
            &::before,
            &::after {
              right: 60px;
            }
          }

          &:nth-child(4) {
            &::before,
            &::after {
              right: 75px;
            }
          }

          &:nth-child(5) {
            &::before,
            &::after {
              right: 100px;
            }
          }

          &:nth-child(6) {
            &::before,
            &::after {
              right: 103px;
            }
          }

          &:nth-child(7) {
            &::before,
            &::after {
              right: 110px;
            }
          }
        }
      }

      [aria-sort="none"],
      [aria-sort="descending"] {
        background-image: none;

        &::before {
          color: #bbb;
        }
      }

      [aria-sort="none"],
      [aria-sort="ascending"] {
        background-image: none;

        &::after {
          color: #bbb;
        }
      }

      th.table-b-table-default {
        padding-top: 6px;
        padding-bottom: 6px;
        background-color: #d6ecff;
        border: none;
        color: #646464;
        font-weight: normal;

        &:first-child {
          border-top-right-radius: 2px;
          border-bottom-right-radius: 2px;
        }

        &:last-child {
          border-top-left-radius: 2px;
          border-bottom-left-radius: 2px;
          padding-left: 28px;
        }
      }
    }
  }

  .pagination-wrap {
    .b-pagination {
      direction: rtl;
      text-align: center;
      display: block;
      margin: 0;
      padding: 0 0 0.5rem 0;

      li {
        display: inline-block;
        margin: 0;

        &.text-btn {
          padding: 0.6rem 0.6rem 0;

          button {
            background-color: #e3e3e3;
            color: black;
            font-size: 14px;
            padding: 7px;
          }

          span {
            display: none;
          }
        }

        &.num-btn {
          .page-link {
            background: none;
            border-color: transparent;
            color: black;
            padding: 0.3rem;
            font-size: 14px;

            &:focus {
              box-shadow: none;
            }
          }

          &.active {
            .page-link {
              color: #9a9a9a;
              text-decoration: underline;
            }
          }
        }
      }
    }
  }
}

.nav-tabs .nav-link:focus,
.nav-tabs .nav-link:hover {
  background: #c8e3fb;
  border-color: #c8e3fb;
}

.nav-link.active {
  background: #fbfbfb !important;
  border-color: #fbfbfb !important;
}

.nav-tabs {
  background: #d6ecff;
}

.tab-content {
  min-height: 400px;
}

.tooltip-inner {
  max-width: 400px;
}
</style>
